<script>
    import { clearText } from '@/scripts/tools.js';
    import { getImageDescription, classifyImage } from '@/api/chatGPT.js';

    export default {
        data() {
            return {
                selectPromt: null,
                requestingAll: false,
                requestAllDone: false,
            }
        },
        props: {
            file: Object,
        },
        computed: {
            prompts() { return this.$store.getters.PROMPTS },
            defaultPrompt() { return this.$store.getters.DEFAULT_PROMPT },
            requestAllLabel() {
                if (this.requestingAll) return '⏳ Working…';
                if (this.requestAllDone) return '✓ Filled';
                return '✨ Request All';
            },
        },
        async mounted() {
            this.selectPromt = this.defaultPrompt;
            await this.loadBaseAdditionalTags();
        },
        methods: {
            async loadBaseAdditionalTags() {
                const textarea = document.getElementById(`${this.file.id}-additional-tags`);
                textarea.value = this.file.additionalTags;
            },
            async updateAdditionalTags(event) {
                await this.file.setAdditionalTags(event.target.value);
            },
            async requestChatGPTText() {
                const response = await getImageDescription(this.file.imagePreview, this.selectPromt);
                await this.file.setAdditionalTags(await clearText(`${this.file.additionalTags}, ${response}`));
            },
            async methodClearText() {
                const newText = await clearText(this.file.additionalTags);
                await this.file.setAdditionalTags(newText);
            },
            async requestAll() {
                // Full image enrichment: properties + tags + additional tags, the
                // SAME thing the backend does when GUP uploads an image.
                const assetTypeId = this.$store.getters.ASSET_TYPE_IMAGES;
                const schema = this.$store.getters.FILTER_BY_ASSET_TYPE(assetTypeId);
                console.debug('[requestAll] start', { fileId: this.file.id, assetTypeId, prompt: this.selectPromt });
                this.requestingAll = true;
                try {
                    const res = await classifyImage(this.file.imagePreview, assetTypeId, this.selectPromt);
                    if (res == null) return;
                    await this.file.setSelectFilter(res.filter_item_ids ?? [], schema);
                    await this.file.setTags(res.tags_ids ?? []);
                    await this.file.setAdditionalTags(
                        await clearText(`${this.file.additionalTags}, ${res.additional_tags ?? ''}`)
                    );
                    console.debug('[requestAll] applied', {
                        fileId: this.file.id,
                        properties: (res.filter_item_ids ?? []).length,
                        tags: (res.tags_ids ?? []).length,
                    });
                    this.requestAllDone = true;
                    setTimeout(() => { this.requestAllDone = false; }, 2000);
                } finally {
                    this.requestingAll = false;
                }
            }
        },
        watch: {
            'file.additionalTags': {
                handler: async function() {
                    this.$nextTick(async () => {
                        await this.loadBaseAdditionalTags();
                    });
                }
            }
        }
    }
</script>


<template>
    <div class="image_additional_tags">
        <div class="mb_5">
            <label>Additional Tags:</label>
        </div>
        <textarea
            :id = "`${file.id}-additional-tags`"
            class = "mb_5"
            @change="updateAdditionalTags"
        ></textarea>
        <div class="image_at_footer">
            <label>ChatGPT:</label>
            <select 
                class="ml_5" 
                v-model="selectPromt"
            >
                <option
                    v-for="(promptValue, promptIndex) in prompts" :key="promptIndex"
                    :value="promptValue.id"
                >
                    {{ promptValue.name }}
                </option>
            </select>
            <button 
                class="button_blue_150 ml_5"
                @click="requestChatGPTText"
            >Request</button>
            <button
                class="button_blue_150 ml_5"
                @click="methodClearText"
            >Сheck Text</button>
            <span class="request_all_hint_wrap ml_5">
                <button
                    class="button_request_all"
                    :class="{ is_done: requestAllDone }"
                    :disabled="requestingAll"
                    @click="requestAll"
                >{{ requestAllLabel }}</button>
                <span class="request_all_hint_bubble">
                    Auto-fills from the image (same as GUP upload):
                    Properties · Tags · Additional tags.
                </span>
            </span>
        </div>
    </div>
</template>


<style scoped>
    .image_additional_tags {
        padding: 10px;
        padding-top: 5px;
    }
    .image_at_footer {
        display: flex;
        flex-direction: row;
    }

    textarea {
        width: 100%;
        resize: vertical;
        min-height: 3em;
        border-radius: 5px;

        font-size: 14px;

        border: var(--border);
    }
    select {
        width: 200px;
        border-radius: 5px;
    }

    /* Request All — the accent action of the row (fills properties + tags + add. tags). */
    .request_all_hint_wrap {
        position: relative;
        display: inline-flex;
    }
    .button_request_all {
        width: 150px;
        border-radius: 5px;
        cursor: pointer;
        color: #ffffff;
        background: #6d28d9;            /* accent, distinct from the blue secondary buttons */
        border: 1px solid #5b21b6;
        font-weight: 600;
        transition: background 0.15s ease;
    }
    .button_request_all:hover:not(:disabled) {
        background: #5b21b6;
    }
    .button_request_all:disabled {
        opacity: 0.7;
        cursor: default;
    }
    .button_request_all.is_done {
        background: #16a34a;            /* green success flash for "✓ Filled" */
        border-color: #15803d;
    }
    /* Custom hover tooltip ABOVE the button (not a native title). */
    .request_all_hint_bubble {
        position: absolute;
        bottom: calc(100% + 6px);
        right: 0;
        z-index: 100;
        width: 240px;
        background: #1e293b;
        color: #f1f5f9;
        font-size: 11px;
        line-height: 1.5;
        padding: 6px 8px;
        border-radius: 6px;
        pointer-events: none;
        opacity: 0;
        transition: opacity 0.15s ease 0.1s;   /* small delay so it doesn't flicker on pass-through */
        white-space: normal;
        font-weight: 400;
    }
    .request_all_hint_bubble::after {
        content: '';
        position: absolute;
        top: 100%;
        right: 16px;
        border: 5px solid transparent;
        border-top-color: #1e293b;
    }
    .request_all_hint_wrap:hover .request_all_hint_bubble {
        opacity: 1;
    }
</style>